import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../ui/use-toast';
import { X, UserPlus, Trash2, Loader2, Mail, User } from 'lucide-react';
import { competitionsService } from '../../services/competitions';
import { ParticipantLabel } from './ParticipantLabel';
import type { CompetitionParticipant, CompetitionCategory, CompetitionTeam } from '../../types/competition';

interface CompetitionParticipantsPanelProps {
  competitionId: string;
  onClose: () => void;
}

export default function CompetitionParticipantsPanel({
  competitionId,
  onClose
}: CompetitionParticipantsPanelProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [participants, setParticipants] = useState<CompetitionParticipant[]>([]);
  const [categories, setCategories] = useState<CompetitionCategory[]>([]);
  const [teams, setTeams] = useState<CompetitionTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newSurname, setNewSurname] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [participantsData, categoriesData, teamsData] = await Promise.all([
        competitionsService.getParticipants(competitionId),
        competitionsService.getCategories(competitionId),
        competitionsService.getTeams(competitionId)
      ]);
      setParticipants(participantsData);
      setCategories(categoriesData);
      setTeams(teamsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast({
        title: t('competitionParticipants.fetchError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [competitionId, t, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddParticipant = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newEmail.trim()) {
      return;
    }

    setAddingParticipant(true);
    try {
      const newParticipant = await competitionsService.addParticipant(competitionId, {
        email: newEmail.trim(),
        name: newName.trim() || undefined,
        surname: newSurname.trim() || undefined,
        competition_category_id: selectedCategoryId || undefined,
        competition_team_id: selectedTeamId || undefined,
      });
      setParticipants(prev => [...prev, newParticipant]);
      setNewEmail('');
      setNewName('');
      setNewSurname('');
      setSelectedCategoryId('');
      setSelectedTeamId('');
      toast({
        title: t('competitionParticipants.addSuccess'),
        description: t('competitionParticipants.addSuccessDescription', { email: newEmail }),
      });
    } catch (error) {
      console.error('Failed to add participant:', error);
      toast({
        title: t('competitionParticipants.addError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setAddingParticipant(false);
    }
  };

  const handleRemoveParticipant = async (participant: CompetitionParticipant) => {
    setRemovingId(participant.id);
    try {
      await competitionsService.removeParticipant(competitionId, participant.id);
      setParticipants(prev => prev.filter(p => p.id !== participant.id));
      toast({
        title: t('competitionParticipants.removeSuccess'),
        description: t('competitionParticipants.removeSuccessDescription', { email: participant.email }),
      });
    } catch (error) {
      console.error('Failed to remove participant:', error);
      toast({
        title: t('competitionParticipants.removeError'),
        variant: 'destructive',
      });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />
      <Card className="fixed inset-4 sm:inset-auto sm:fixed sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg z-50 shadow-xl overflow-y-auto max-h-[90vh]">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-semibold">
          {t('competitionParticipants.title')}
        </CardTitle>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add participant form */}
        <form onSubmit={handleAddParticipant} className="space-y-2">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder={t('competitionParticipants.emailPlaceholder')}
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={addingParticipant}
              className="flex-1"
            />
            <Button type="submit" disabled={addingParticipant || !newEmail.trim()}>
              {addingParticipant ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
            </Button>
          </div>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder={t('competitionParticipants.namePlaceholder')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={addingParticipant}
              className="flex-1"
            />
            <Input
              type="text"
              placeholder={t('competitionParticipants.surnamePlaceholder')}
              value={newSurname}
              onChange={(e) => setNewSurname(e.target.value)}
              disabled={addingParticipant}
              className="flex-1"
            />
          </div>
          {categories.length > 0 && (
            <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder={t('competitionParticipants.selectCategory')} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {teams.length > 0 && (
            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
              <SelectTrigger>
                <SelectValue placeholder={t('competitionParticipants.selectTeam')} />
              </SelectTrigger>
              <SelectContent>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </form>

        {/* Participants list */}
        <div className="max-h-80 overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : participants.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('competitionParticipants.noParticipants')}
            </div>
          ) : (
            participants.map((participant) => (
              <div
                key={participant.id}
                className="flex items-center justify-between p-3 bg-muted rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-background flex items-center justify-center">
                    {participant.user ? (
                      <User className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <ParticipantLabel participant={participant} className="font-medium" />
                    <div className="text-sm text-muted-foreground">{participant.email}</div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveParticipant(participant)}
                  disabled={removingId === participant.id}
                  className="h-8 w-8 text-destructive hover:text-destructive"
                >
                  {removingId === participant.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))
          )}
        </div>

        {/* Participant count */}
        {!loading && participants.length > 0 && (
          <div className="text-sm text-muted-foreground text-center pt-2 border-t">
            {t('competitionParticipants.count', { count: participants.length })}
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
}
